import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  HelpCircle,
  Hourglass,
  Loader2,
  Pause,
  UserRound,
  X,
} from 'lucide-react'
import { type ComponentProps, useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Badge } from 'slab'
import { capitalize, getFormattedDate } from 'utils'
import {
  getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey,
  getGetBrokersTaskWorkflowsQueryKey,
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
} from 'utils/web_api'
import {
  type BrokersTask,
  type BrokersWorkflow,
  type BrokersWorkflowStatus,
} from 'utils/web_api.schemas'

import WorkflowMetadataDetails from './WorkflowMetadataDetails'
import WorkflowProgressBar from './WorkflowProgressBar'
import {
  TaskActionControls,
  useTaskActions,
  WorkflowActionControls,
} from './workflows/WorkflowActionControls'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  getTaskCompactStyle,
  TaskStatusBadge,
} from './workflows/WorkflowVisualIndicators'

type IconProp = ComponentProps<typeof Badge>['leftIcon']
type BadgeType = ComponentProps<typeof Badge>['type']

type WorkflowSummaryProps = {
  workflow: BrokersWorkflow
}

const TasksList = ({
  workflow,
  handleTaskAction,
  taskMutation,
  formatTaskType,
}: {
  workflow: BrokersWorkflow
  handleTaskAction: (taskId: string, action: string) => void
  taskMutation: any
  formatTaskType: (type: string) => string
}) => {
  const [isExpanded, setIsExpanded] = useState(workflow.status !== 'completed')

  if (!workflow.tasks_summary || workflow.tasks_summary.length === 0) {
    return null
  }

  const sortedTasks = workflow.tasks_summary.sort((a, b) => {
    const aOrder = a.sequence_order || 0
    const bOrder = b.sequence_order || 0
    return aOrder - bOrder
  })

  // Calculate task summary for collapsed state
  const taskSummary = {
    total: sortedTasks.length,
    completed: sortedTasks.filter((task) => task.status === 'completed').length,
    pending: sortedTasks.filter((task) => task.status === 'pending').length,
    failed: sortedTasks.filter((task) => task.status === 'failed').length,
  }

  const getSummaryText = () => {
    const parts = []
    if (taskSummary.completed > 0) {
      parts.push(`${taskSummary.completed} completed`)
    }
    if (taskSummary.pending > 0) parts.push(`${taskSummary.pending} pending`)
    if (taskSummary.failed > 0) parts.push(`${taskSummary.failed} failed`)
    return parts.join(', ')
  }

  return (
    <div className='space-y-2'>
      {/* Tasks Header with Toggle Button */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-2'>
          <h5 className='font-medium text-sm text-gray-700'>Tasks:</h5>
          <AnimateOpacity isOpen={!isExpanded}>
            <span className='text-xs text-gray-500'>{getSummaryText()}</span>
          </AnimateOpacity>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700 transition-colors'
        >
          <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
          {isExpanded
            ? (
              <ChevronUp className='w-3 h-3' />
              )
            : (
              <ChevronDown className='w-3 h-3' />
              )}
        </button>
      </div>

      {/* Animated Tasks List */}
      <AnimateHeightAndOpacity isOpen={isExpanded}>
        <div className='space-y-1'>
          {sortedTasks.map((task: BrokersTask) => {
            return (
              <div
                key={task.id}
                className={`flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm ${getTaskCompactStyle(
                  task
                )}`}
              >
                <div className='flex items-center space-x-2'>
                  <span className='font-medium'>
                    {task.display_name || formatTaskType(task.task_type || '')}
                  </span>
                  <TaskStatusBadge
                    status={task.status}
                    isExecutable={task.can_execute}
                  />
                  <ExecutionMethodBadge method={task.execution_method} />
                  <ExecutionContextBadge context={task.execution_context} />

                  {/* Completed By Broker */}
                  {task.status === 'completed' && task.completed_by_broker && (
                    <TooltipWrapper tooltipText='Broker who completed the task'>
                      <div className='flex items-center space-x-1 text-xs text-gray-400'>
                        <UserRound className='w-3 h-3' />
                        <span>{task.completed_by_broker.broker_name}</span>
                        {task.completed_at && (
                          <>
                            <span>•</span>
                            <span className='text-xs text-gray-400'>
                              {getFormattedDate(task.completed_at, {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </span>
                          </>
                        )}
                      </div>
                    </TooltipWrapper>
                  )}
                </div>

                <div className='flex items-center space-x-2'>
                  {/* Task Actions for Manual Pending Tasks */}
                  <TaskActionControls
                    task={task}
                    onTaskAction={handleTaskAction}
                    // We can use the mutation state here even though there are multiple manual tasks
                    // because we only display one row at a time.
                    isLoading={taskMutation.isPending}
                    workflowId={workflow.id}
                  />

                  {/* Always show sequence order */}
                  {task.sequence_order && (
                    <span className='text-xs text-gray-400'>
                      #{task.sequence_order}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}

const getWorkflowBorderColor = (status: BrokersWorkflowStatus): string => {
  switch (status) {
    case 'pending':
      return 'border-l-4 border-slate-400 bg-slate-50' // Neutral/slate border - ready to start, no urgency
    case 'in_progress':
      return 'border-l-4 border-blue-400 bg-blue-50' // Info/blue border with subtle background
    case 'completed':
      return 'border-l-4 border-green-400 bg-green-50' // Success/green border with subtle background
    case 'canceled':
      return 'border-l-4 border-red-400 bg-red-50' // Critical/red border with subtle background
    case 'paused':
      return 'border-l-4 border-amber-400 bg-amber-50' // Warning/amber border - needs attention
    default:
      return 'border-l-4 border-gray-400 bg-gray-50' // Default/gray border with subtle background
  }
}

const getWorkflowBadgeMetadata = (
  status: BrokersWorkflowStatus
): { type: BadgeType; text: string; leftIcon: IconProp } => {
  // Create spinning loader component for in-progress status
  const SpinningLoader: IconProp = (props: any) => (
    <Loader2 className='animate-spin' {...props} />
  )

  switch (status) {
    case 'pending':
      return { type: 'slate', text: 'Pending', leftIcon: Hourglass }
    case 'in_progress':
      return { type: 'info', text: 'In Progress', leftIcon: SpinningLoader }
    case 'completed':
      return { type: 'success', text: 'Completed', leftIcon: Check }
    case 'canceled':
      return { type: 'critical', text: 'Canceled', leftIcon: X }
    case 'paused':
      return { type: 'warning', text: 'Paused', leftIcon: Pause }
    default:
      return { type: 'info', text: 'Unknown', leftIcon: HelpCircle }
  }
}

const WorkflowStatusBadge = ({ status }: { status: BrokersWorkflowStatus }) => {
  const { type, text, leftIcon } = getWorkflowBadgeMetadata(status)

  return (
    <Badge type={type} bordered leftIcon={leftIcon}>
      {text}
    </Badge>
  )
}

const WorkflowLinkBadge = ({ workflow }: { workflow: BrokersWorkflow }) => {
  return (
    <Badge
      type='info'
      bordered
      actionIcon={ExternalLink}
      onActionClick={() => {
        window.open(`/brokers/workflows/${workflow.id}`, '_blank')
      }}
    >
      View Workflow
    </Badge>
  )
}

const WorkflowSummary = ({ workflow }: WorkflowSummaryProps) => {
  const { memberId } = useContext(BrokersMemberContext)
  const queryClient = useQueryClient()

  const formatWorkflowType = (type: string) => {
    return type.split('_').map(capitalize).join(' ')
  }

  const formatTaskType = (type: string) => {
    return type
      .replace(/Tasks::V2::/, '')
      .replace(/Task$/, '')
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }

  // Task actions hook with additional cache invalidation
  const { handleTaskAction, isLoading: isTaskLoading } = useTaskActions(
    workflow.id,
    {
      onSuccess: () => {
        // Also invalidate the workflow list query for this enrollment
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTaskWorkflowsQueryKey({
            'filters[enrollment_ids][]': [workflow.insurance_enrollment_id],
            'sort[status]': 'asc',
          }),
        })
      },
    }
  )

  // Handler for additional cache invalidation specific to WorkflowSummary context
  const handleWorkflowActionSuccess = () => {
    // Invalidate the unprocessed enrollment cache.
    // Technically we need to wait for the workflow to execute a few tasks before updating the cache, working on a
    // better solution for this but for now this is a workaround.
    // TODO: Remove this once we have a better solution.
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(
          workflow.insurance_enrollment_id
        ),
      })
      queryClient.invalidateQueries({
        queryKey:
          getGetBrokersMembersMemberIdStripeIssuingCardsQueryKey(memberId),
      })
    }, 2000)
  }

  const completedTasks = workflow.completed_task_count || 0
  const totalTasks = workflow.task_count || 0
  const failedTasks = workflow.failed_task_count || 0

  return (
    <div
      className={`border rounded-lg p-4 space-y-3 transition-all duration-300 ${getWorkflowBorderColor(
        workflow.status
      )}`}
    >
      {/* Workflow Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center space-x-3'>
          <h4 className='font-semibold text-lg'>
            {formatWorkflowType(workflow.workflow_type)}
          </h4>
          <WorkflowStatusBadge status={workflow.status} />
          <WorkflowLinkBadge workflow={workflow} />
        </div>

        <div className='flex items-center space-x-3'>
          <div className='text-sm text-gray-500'>
            {workflow.progress_percentage?.toFixed(0)}% Complete
          </div>

          {/* Action Controls */}
          <WorkflowActionControls
            workflow={workflow}
            onSuccess={handleWorkflowActionSuccess}
          />
        </div>
      </div>

      {/* Progress Bar */}
      {workflow.tasks_summary && workflow.tasks_summary.length > 0 && (
        <WorkflowProgressBar tasks={workflow.tasks_summary} />
      )}

      {/* Progress Summary */}
      <div className='flex items-center space-x-4 text-sm'>
        <span className='text-gray-600'>
          {completedTasks}/{totalTasks} tasks completed
        </span>
        {failedTasks > 0 && (
          <span className='text-red-600'>{failedTasks} failed</span>
        )}
        {workflow.started_at && (
          <span className='text-gray-500'>
            Started {new Date(workflow.started_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Workflow Details */}
      <WorkflowMetadataDetails workflow={workflow} headerVariant='compact' />

      {/* Tasks List */}
      <TasksList
        workflow={workflow}
        handleTaskAction={handleTaskAction}
        taskMutation={{ isPending: isTaskLoading }}
        formatTaskType={formatTaskType}
      />
    </div>
  )
}

export default WorkflowSummary
